import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Icon } from '@/components/Icon';
import { Icons } from '@/assets';
import './styles.css';
import SubmitButton from '@/ctComponents/submitButton';
import CloseButton from '@/ctComponents/closeButton';

const DatePickerInput = ({
  onChange,
  placeholder = 'Select Date',
  className = '',
  disabled = false,
  minDate = null,
  maxDate = null,
  dateFormat = 'MM-dd-yyyy',
  showTimeSelect = false,
  timeFormat = 'HH:mm',
  timeIntervals = 15,
  value,
  height,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelectedDate, setTempSelectedDate] = useState(value);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        containerRef.current &&
        popupRef.current &&
        !containerRef.current.contains(event.target) &&
        !popupRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Update temp date when selectedDate or value prop changes
  useEffect(() => {
    setTempSelectedDate(value);
  }, [value]);

  const popupRef = useRef(null);

  const calculatePopupPosition = useCallback(() => {
    if (containerRef.current && popupRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popupWidth = popupRef.current.offsetWidth;
      const viewportWidth = window.innerWidth;

      let left = rect.left + window.scrollX + rect.width / 2 - popupWidth / 2;

      if (left < 8) left = 8;
      if (left + popupWidth > viewportWidth - 8) {
        left = viewportWidth - popupWidth - 8;
      }

      setPopupPosition({
        top: rect.bottom + window.scrollY + 8,
        left,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => calculatePopupPosition());
    }
  }, [isOpen, calculatePopupPosition]);

  // Handle scroll to close date picker when input is too close to top
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
      // Close date picker when bottom of input is less than 64px from top of viewport
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();

        if (rect.bottom < 64) {
          setIsOpen(false);
        }
      }
    };

    // Add scroll listeners to all scrollable parents
    const scrollableElements = [];
    let element = containerRef.current?.parentElement;

    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      if (
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll'
      ) {
        element.addEventListener('scroll', handleScroll, { passive: true });
        scrollableElements.push(element);
      }
      element = element.parentElement;
    }

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      scrollableElements.forEach((el) => {
        el.removeEventListener('scroll', handleScroll);
      });
    };
  }, [isOpen]);

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setTempSelectedDate(value);
      calculatePopupPosition();
      setIsOpen(true);
    }
  }, [disabled, value, calculatePopupPosition]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSubmit = useCallback(() => {
    onChange(tempSelectedDate);
    setIsOpen(false);
  }, [tempSelectedDate, onChange]);

  const formatDisplayDate = useCallback(
    (date) => {
      if (!date) return '';

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      if (showTimeSelect) {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${month}-${day}-${year} ${hours}:${minutes}`;
      }

      return `${month}-${day}-${year}`;
    },
    [showTimeSelect]
  );

  return (
    <>
      <div className={`relative ${className}`} ref={containerRef}>
        {/* Input Display */}
        <div
          style={{
            height,
          }}
          className={`
            w-full h-38 border-solid px-3 border border-border-tertiary rounded box-border
            bg-white cursor-pointer flex items-center justify-between transition-all
            hover:shadow-button-hover hover:border-black focus:outline-none focus:border-transparent
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}
            ${isOpen ? 'shadow-button-hover ' : ''}
          `}
          onClick={handleInputClick}
          {...props}>
          <span
            className={`text-13 ${
              tempSelectedDate ? 'text-gray-900' : 'text-gray-400'
            }`}>
            {tempSelectedDate
              ? formatDisplayDate(tempSelectedDate)
              : placeholder}
          </span>
          <Icon
            src={Icons.calendarday}
            width={16}
            height={16}
            color="#6B7280"
          />
        </div>
      </div>

      {/* Date Picker Popup - Rendered as Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={popupRef}
            style={{
              position: 'absolute',
              top: popupPosition.top,
              left: popupPosition.left,
              width: '320px', // w-80 equivalent
              zIndex: 9999,
            }}
            className="p-3 rounded-t-md rounded-b-xl border-border-tertiary shadow-date-picker bg-white border border-solid">
            <div className="custom-datepicker-wrapper">
              <DatePicker
                selected={tempSelectedDate}
                onChange={(date) => setTempSelectedDate(date)}
                inline
                className="border-none datepicker-input-calendar"
                showPopperArrow={false}
                calendarClassName="datepicker-input-calendar"
                minDate={minDate}
                maxDate={maxDate}
                showTimeSelect={showTimeSelect}
                timeFormat={timeFormat}
                timeIntervals={timeIntervals}
                dateFormat={dateFormat}
              />
            </div>
            <div className="flex justify-center items-stretch gap-2 border-t border-gray-200 mt-3">
              <CloseButton
                color="#131517"
                onClick={handleCancel}
                text="Cancel"
                className="flex-1"
              />
              <SubmitButton
                onClick={handleSubmit}
                text="Submit"
                className="flex-1"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default DatePickerInput;
